/**
 * TrustScoreWidget Component
 * 
 * Comprehensive trust score display for provider profiles.
 * Shows:
 * - Trust score (0-100)
 * - Verification badge
 * - Response speed indicator
 * - Completed jobs count
 * - Cancellation rate
 * 
 * Mobile-friendly and visually simple.
 */

import React, { useState, useEffect } from 'react';
import { 
  Shield, CheckCircle2, Clock, Briefcase, XCircle,
  Star, Award, Phone, Mail, Building2, TrendingUp
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { TrustScoreBadge } from './TrustScoreBadge';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// Response speed labels
function getResponseSpeedLabel(avgHours, t) {
  if (!avgHours || avgHours === 0) return { label: t ? t('trustWidget.noData') : 'No data', color: 'text-gray-400' };
  if (avgHours <= 1) return { label: t ? t('trustWidget.veryFast') : 'Very fast', color: 'text-green-600' };
  if (avgHours <= 4) return { label: t ? t('trustWidget.fast') : 'Fast', color: 'text-green-500' };
  if (avgHours <= 12) return { label: t ? t('trustWidget.normal') : 'Normal', color: 'text-blue-500' };
  if (avgHours <= 24) return { label: t ? t('trustWidget.slow') : 'Slow', color: 'text-yellow-600' };
  return { label: t ? t('trustWidget.verySlow') : 'Very slow', color: 'text-orange-500' };
}

// Cancellation rate color
function getCancellationRateColor(rate) {
  if (rate <= 5) return 'text-green-600';
  if (rate <= 15) return 'text-yellow-600';
  return 'text-red-500';
}

export function TrustScoreWidget({
  userId,
  initialData = null,
  variant = 'full', // 'full' | 'compact' | 'mini'
  className = '',
  t // translation function
}) {
  const [trustData, setTrustData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!initialData && userId) {
      fetchTrustScore();
    }
  }, [userId, initialData]);

  const fetchTrustScore = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/reputation/trust-score/${userId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch trust score');
      }
      
      const data = await response.json();
      setTrustData(data);
    } catch (err) {
      console.error('Trust score fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Mini variant - just the badge
  if (variant === 'mini') {
    if (loading) return <Skeleton className="h-5 w-16 rounded-full" />;
    if (error || !trustData) return null;
    
    return (
      <TrustScoreBadge 
        score={trustData.trust_score} 
        size="sm"
        t={t}
        className={className}
      />
    );
  }

  // Compact variant - badge with key metrics inline
  if (variant === 'compact') {
    if (loading) {
      return (
        <div className={cn("flex items-center gap-2", className)}>
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-4 w-16" />
        </div>
      );
    }
    
    if (error || !trustData) return null;
    
    const responseSpeed = getResponseSpeedLabel(trustData.response_time_hours, t);
    
    return (
      <div className={cn("flex flex-wrap items-center gap-2 text-sm", className)}>
        <TrustScoreBadge score={trustData.trust_score} size="sm" t={t} />
        
        {trustData.identity_verified && (
          <span className="flex items-center gap-0.5 text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            <span className="text-xs">{t ? t('trustWidget.verified') : 'Verified'}</span>
          </span>
        )}
        
        {trustData.total_jobs_completed > 0 && (
          <span className="flex items-center gap-0.5 text-gray-600">
            <Briefcase className="h-3 w-3" />
            <span className="text-xs">{trustData.total_jobs_completed}</span>
          </span>
        )}
        
        {trustData.average_rating > 0 && (
          <span className="flex items-center gap-0.5 text-yellow-600">
            <Star className="h-3 w-3 fill-yellow-500" />
            <span className="text-xs">{trustData.average_rating.toFixed(1)}</span>
          </span>
        )}
      </div>
    );
  }

  // Full variant - complete widget
  if (loading) {
    return (
      <Card className={cn("", className)} data-testid="trust-score-widget-loading">
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-2 w-full" />
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !trustData) {
    return null;
  }

  const responseSpeed = getResponseSpeedLabel(trustData.response_time_hours, t);
  const cancellationRate = trustData.cancellation_rate || 0;
  const cancellationColor = getCancellationRateColor(cancellationRate);

  return (
    <Card className={cn("", className)} data-testid="trust-score-widget">
      <CardContent className="p-4">
        {/* Header with score */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Trust Score Circle */}
            <div className="relative">
              <div className={cn(
                "h-12 w-12 rounded-full flex items-center justify-center font-bold text-lg",
                trustData.trust_score >= 80 ? "bg-amber-100 text-amber-700" :
                trustData.trust_score >= 60 ? "bg-green-100 text-green-700" :
                trustData.trust_score >= 30 ? "bg-blue-100 text-blue-700" :
                "bg-gray-100 text-gray-700"
              )} data-testid="trust-score-circle">
                {Math.round(trustData.trust_score)}
              </div>
              {trustData.has_trusted_pro_badge && (
                <Award className="absolute -top-1 -right-1 h-5 w-5 text-amber-500" data-testid="trusted-pro-badge" />
              )}
            </div>
            
            <div>
              <TrustScoreBadge score={trustData.trust_score} t={t} />
              <p className="text-xs text-gray-500 mt-0.5">
                {t ? t('trustWidget.trustScore') : 'Trust Score'}
              </p>
            </div>
          </div>
          
          {/* Verification badges */}
          <div className="flex flex-col gap-1" data-testid="verification-badges">
            {trustData.identity_verified && (
              <span className="flex items-center gap-1 text-xs text-green-600" data-testid="email-verified-badge">
                <Mail className="h-3 w-3" />
                {t ? t('trustWidget.emailVerified') : 'Email'}
              </span>
            )}
            {trustData.phone_verified && (
              <span className="flex items-center gap-1 text-xs text-green-600" data-testid="phone-verified-badge">
                <Phone className="h-3 w-3" />
                {t ? t('trustWidget.phoneVerified') : 'Phone'}
              </span>
            )}
            {trustData.company_verified && (
              <span className="flex items-center gap-1 text-xs text-green-600" data-testid="company-verified-badge">
                <Building2 className="h-3 w-3" />
                {t ? t('trustWidget.companyVerified') : 'Company'}
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <Progress 
          value={trustData.trust_score} 
          className="h-1.5 mb-4" 
          data-testid="trust-score-progress"
        />

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="trust-metrics-grid">
          {/* Response Speed */}
          <div className="bg-gray-50 rounded-lg p-2.5" data-testid="metric-response-speed">
            <div className="flex items-center gap-1.5 text-gray-600 mb-1">
              <Clock className="h-3.5 w-3.5" />
              <span className="text-xs">{t ? t('trustWidget.responseSpeed') : 'Response'}</span>
            </div>
            <p className={cn("font-medium text-sm", responseSpeed.color)} data-testid="response-speed-value">
              {responseSpeed.label}
            </p>
          </div>

          {/* Completed Jobs */}
          <div className="bg-gray-50 rounded-lg p-2.5" data-testid="metric-completed-jobs">
            <div className="flex items-center gap-1.5 text-gray-600 mb-1">
              <Briefcase className="h-3.5 w-3.5" />
              <span className="text-xs">{t ? t('trustWidget.completedJobs') : 'Jobs Done'}</span>
            </div>
            <p className="font-medium text-sm text-gray-900" data-testid="completed-jobs-value">
              {trustData.total_jobs_completed || 0}
            </p>
          </div>

          {/* Average Rating */}
          <div className="bg-gray-50 rounded-lg p-2.5" data-testid="metric-rating">
            <div className="flex items-center gap-1.5 text-gray-600 mb-1">
              <Star className="h-3.5 w-3.5" />
              <span className="text-xs">{t ? t('trustWidget.rating') : 'Rating'}</span>
            </div>
            <p className="font-medium text-sm text-yellow-600" data-testid="rating-value">
              {trustData.average_rating > 0 ? (
                <>
                  <Star className="h-3 w-3 inline fill-yellow-500 mr-0.5" />
                  {trustData.average_rating.toFixed(1)}
                </>
              ) : (
                <span className="text-gray-400">{t ? t('trustWidget.noRatings') : 'No ratings'}</span>
              )}
            </p>
          </div>

          {/* Cancellation Rate */}
          <div className="bg-gray-50 rounded-lg p-2.5" data-testid="metric-cancellations">
            <div className="flex items-center gap-1.5 text-gray-600 mb-1">
              <XCircle className="h-3.5 w-3.5" />
              <span className="text-xs">{t ? t('trustWidget.cancellations') : 'Cancellations'}</span>
            </div>
            <p className={cn("font-medium text-sm", cancellationColor)} data-testid="cancellation-rate-value">
              {cancellationRate.toFixed(0)}%
            </p>
          </div>
        </div>

        {/* Member Since */}
        {trustData.member_since && (
          <p className="text-xs text-gray-400 mt-3 text-center" data-testid="member-since">
            {t ? t('trustWidget.memberSince') : 'Member since'} {new Date(trustData.member_since).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default TrustScoreWidget;
